/**
 * Pure Coach conversation engine. Given the current state and a new
 * user message, returns the next reply plus any draft fields to
 * commit. No side effects — the Zustand store is what commits them.
 */

import { TREES, type CoachDomain, type DomainTree, type DraftShape } from './trees';

export type CoachStage =
  | 'greeting'       // waiting for first user message
  | 'disambiguate'   // first message matched multiple / none; asking which
  | 'asking_q1'      // domain picked, waiting on Q1 answer
  | 'asking_q2'      // waiting on Q2 answer
  | 'ready';         // draft is complete

export interface CoachState {
  stage: CoachStage;
  domain: CoachDomain | null;
  answers: { q1?: string; q2?: string };
}

export interface CoachReplyPatch {
  reply_key: string;                 // i18n key for Coach's next message
  reply_params?: Record<string, unknown>;
  next: CoachState;
  draft?: DraftShape;                // set on ready
}

/** Entry point: given current state + new user text, compute next. */
export function advance(state: CoachState, user_text: string): CoachReplyPatch {
  const text = user_text.trim();
  if (text.length === 0) {
    return {
      reply_key: 'engine.empty_input',
      next: state,
    };
  }

  switch (state.stage) {
    case 'greeting':
      return onFirstMessage(text);
    case 'disambiguate':
      return onDisambiguate(text);
    case 'asking_q1':
      return onQ1(state, text);
    case 'asking_q2':
      return onQ2(state, text);
    case 'ready':
      // User kept talking after we were done — stay in ready, echo a
      // gentle reminder that they can hit Spark.
      return {
        reply_key: 'engine.already_ready',
        next: state,
      };
  }
}

export function initialState(): CoachState {
  return { stage: 'greeting', domain: null, answers: {} };
}

/** Quick-start-chip shortcut: jump to a specific domain without
 *  parsing a user message. */
export function jumpToDomain(domain: CoachDomain): CoachReplyPatch {
  const tree = TREES[domain];
  return {
    reply_key: 'engine.ack_then_q1',
    reply_params: { ack_key: tree.acknowledgement_key, q1_key: tree.q1_key },
    next: { stage: 'asking_q1', domain, answers: {} },
  };
}

// ─── Stage handlers ───────────────────────────────────────────────

function onFirstMessage(text: string): CoachReplyPatch {
  const hits = detectDomains(text);
  if (hits.length === 1) {
    const tree = TREES[hits[0]!];
    return {
      reply_key: 'engine.ack_then_q1',
      reply_params: { ack_key: tree.acknowledgement_key, q1_key: tree.q1_key },
      next: { stage: 'asking_q1', domain: tree.id, answers: {} },
    };
  }
  if (hits.length === 0 || hits.length > 1) {
    return {
      reply_key: 'engine.disambiguate',
      next: { stage: 'disambiguate', domain: null, answers: {} },
    };
  }
  // unreachable
  return {
    reply_key: 'engine.disambiguate',
    next: { stage: 'disambiguate', domain: null, answers: {} },
  };
}

function onDisambiguate(text: string): CoachReplyPatch {
  const hits = detectDomains(text);
  if (hits.length >= 1) {
    const tree = TREES[hits[0]!];
    return {
      reply_key: 'engine.ack_then_q1',
      reply_params: { ack_key: tree.acknowledgement_key, q1_key: tree.q1_key },
      next: { stage: 'asking_q1', domain: tree.id, answers: {} },
    };
  }
  return {
    reply_key: 'engine.disambiguate_retry',
    next: { stage: 'disambiguate', domain: null, answers: {} },
  };
}

function onQ1(state: CoachState, text: string): CoachReplyPatch {
  if (!state.domain) return { reply_key: 'engine.internal_error', next: state };
  const tree = TREES[state.domain];
  return {
    reply_key: 'engine.ask_q2',
    reply_params: { q2_key: tree.q2_key },
    next: {
      stage: 'asking_q2',
      domain: state.domain,
      answers: { q1: text },
    },
  };
}

function onQ2(state: CoachState, text: string): CoachReplyPatch {
  if (!state.domain) return { reply_key: 'engine.internal_error', next: state };
  const tree = TREES[state.domain];
  const a1 = state.answers.q1 ?? '';
  const draft = tree.toDraft(a1, text);
  return {
    reply_key: 'engine.ready',
    reply_params: { ready_key: tree.ready_key },
    next: {
      stage: 'ready',
      domain: state.domain,
      answers: { q1: a1, q2: text },
    },
    draft,
  };
}

// ─── Keyword detection ────────────────────────────────────────────

function detectDomains(text: string): CoachDomain[] {
  const lower = text.toLowerCase();
  const hits: CoachDomain[] = [];
  (Object.keys(TREES) as CoachDomain[]).forEach((id) => {
    if (matches(lower, TREES[id]!.keywords)) hits.push(id);
  });
  return hits;
}

function matches(haystack: string, needles: string[]): boolean {
  for (const n of needles) {
    if (haystack.includes(n.toLowerCase())) return true;
  }
  return false;
}

// Silent re-export anchor so `DomainTree` stays imported for
// downstream callers reaching through this module.
export type { DomainTree, DraftShape };
